import { useMutation, useQueryClient } from '@tanstack/react-query';
import useUpdateChargingConnectorMetadata from '../../../hooks/charging-connectors/useUpdateChargingConnectorMetadata.ts';
import useUpdateChargingConnectorChargingRate from '../../../hooks/charging-connectors/useUpdateChargingConnectorChargingRate.ts';
import MutateChargingConnectorForm, {
  MutateChargingConnectorFormValues,
} from '../../ui/places/MutateChargingConnectorForm.tsx';
import {
  UpdateChargingConnectorChargingRateRequest,
  UpdateChargingConnectorMetadataRequest,
} from '../../../api/requests/charging-connector-request.ts';
import { notifySuccess } from '../../../utils/NotifySuccess.tsx';
import { notifyError } from '../../../utils/NotifyError.tsx';
import { LoadingOverlay, Modal } from '@mantine/core';
import useChargingConnector from '../../../hooks/charging-connectors/useChargingConnector.ts';
import { ErrorResponse } from '../../../api/responses/error-response.ts';

interface UpdateChargingConnectorModalProps {
  opened: boolean;
  onClose: () => void;
  serviceId: string;
  evseId: string;
  connectorId: string;
  onSuccess: () => void;
}

export function UpdateChargingConnectorModal({
  opened,
  onClose,
  serviceId,
  evseId,
  connectorId,
  onSuccess,
}: UpdateChargingConnectorModalProps) {
  const queryClient = useQueryClient();

  const { data: connector, isLoading: isConnectorLoading } =
    useChargingConnector({ evseId, connectorId });

  const { mutateAsync: updateChargingConnectorMetadata } =
    useUpdateChargingConnectorMetadata();
  const { mutateAsync: updateChargingConnectorChargingRate } =
    useUpdateChargingConnectorChargingRate();

  const {
    mutate: handleSubmit,
    isPending: isUpdateMetadataAndChargingRatePending,
  } = useMutation<void, ErrorResponse, MutateChargingConnectorFormValues>({
    mutationFn: async (formValues) => {
      const updateChargingConnectorMetadataRequest: UpdateChargingConnectorMetadataRequest =
        {
          evseId: evseId,
          connectorId: connectorId,
          connectorSerialNumber: formValues.connectorSerialNumber,
          connectorType: formValues.connectorType,
          isEVCCIDSupported: formValues.isEVCCIDSupported
            ? formValues.isEVCCIDSupported
            : 'UNKNOWN',
          powerType: formValues.powerType ? formValues.powerType : 'UNKNOWN',
          maxPower: {
            value: formValues.maxPowerValue,
            unit: 'kW',
          },
        };

      await updateChargingConnectorMetadata(
        updateChargingConnectorMetadataRequest,
      );

      const updateChargingConnectorChargingRateRequest: UpdateChargingConnectorChargingRateRequest =
        {
          evseId: evseId,
          connectorId: connectorId,
          chargingRateName: null,
          chargingRateDescription: null,
          chargingRateText: formValues.rateText,
          chargingRateEffectivePeriodStartDate: null,
          chargingRateEffectivePeriodEndDate: null,
        };

      await updateChargingConnectorChargingRate(
        updateChargingConnectorChargingRateRequest,
      );
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['chargingServices', serviceId, 'evses'],
        exact: true,
      });

      await queryClient.invalidateQueries({
        queryKey: ['evses', evseId],
        exact: true,
      });

      await queryClient.invalidateQueries({
        queryKey: ['evses', evseId, 'chargingConnectors', connectorId],
        exact: true,
      });

      notifySuccess('更新充電槍成功');
      onSuccess?.();
    },
    onError: (error) => {
      notifyError('更新充電槍失敗', error.message);
    },
  });

  const handleCancel = () => {
    onClose();
  };

  if (isConnectorLoading) return <LoadingOverlay visible={false} />;

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      closeOnClickOutside={false}
      title={'更新充電資訊 - 充電槍'}
      centered
      size={659}
      styles={{
        title: { fontWeight: 700, fontSize: 16 },
        header: { paddingBottom: 24 },
      }}
    >
      <MutateChargingConnectorForm
        initialValues={{
          connectorSerialNumber: connector.connectorSerialNumber,
          connectorType: connector.connectorType,
          maxPowerValue: connector.maxPower.value,
          powerType: connector.powerType,
          isEVCCIDSupported: connector.isEVCCIDSupported,
          rateText: connector.chargingRate.rateText,
        }}
        onSubmit={handleSubmit}
        onCancel={handleCancel}
        isSubmitting={isUpdateMetadataAndChargingRatePending}
        submitButtonText={'更新充電槍'}
      />
    </Modal>
  );
}
